"use client";

import { useCallback, useMemo, useState } from "react";
import type { VisitorStorage } from "../visitor-storage";
import type { MessageVariant } from "../widget-config";
import {
  localKeyId,
  localSlotKeyId,
  readLocalSlots,
  rememberLocalSlot,
  splitLocalKeyId,
} from "./local-slots";
import { type LocalSlots, slotKeyFor } from "./local-slots-context";

/**
 * The shell's local-slot state. A slot is local when it was injected (host props / unsaved preview, once
 * the BE session does not list it, from the first render), when the BE answered NOT_FOUND for it, or when its thread
 * header snapshot was persisted. Preview / demo without a BE session (`enabled: false`) keeps the
 * reserved `message:` keys: nothing is sent there.
 */
export function useLocalSlotsState(opts: {
  enabled: boolean;
  storage: VisitorStorage;
  publicKey: string | null;
  injected: ReadonlySet<string>;
  /** MESSAGE slot ids of the BE session projection, or `null` when there is none. */
  sessionIds: ReadonlySet<string> | null;
  /** The BE has the `variantThreads` capability: a slot thread is per variant. */
  variantThreads?: boolean;
}): LocalSlots & {
  markLocal: (slotId: string, variant?: MessageVariant, pinVariant?: boolean) => void;
} {
  const { enabled, storage, publicKey, injected, sessionIds, variantThreads = false } = opts;
  const pk = publicKey ?? "";
  const [snaps, setSnaps] = useState(() => readLocalSlots(storage, pk));
  const [notFound, setNotFound] = useState<ReadonlySet<string>>(() => new Set());

  const isLocal = useCallback(
    (slotId: string) =>
      enabled &&
      (Object.keys(snaps).some((k) => splitLocalKeyId(k).slotKeyId === localSlotKeyId(slotId)) ||
        notFound.has(slotId) ||
        (injected.has(slotId) && !sessionIds?.has(slotId))),
    [enabled, snaps, notFound, injected, sessionIds],
  );
  const remember = useCallback(
    (slotId: string, variant: MessageVariant, pinVariant = false) =>
      setSnaps(rememberLocalSlot(storage, pk, { slotId, variant, pinVariant })),
    [storage, pk],
  );
  const markLocal = useCallback(
    (slotId: string, variant?: MessageVariant, pinVariant = false) => {
      setNotFound((prev) => new Set(prev).add(slotId));
      if (variant) remember(slotId, variant, pinVariant);
    },
    [remember],
  );
  return useMemo(
    () => ({
      isLocal,
      snapshotOf: (keyId) => {
        const { slotKeyId, variantKeyId } = splitLocalKeyId(keyId);
        return snaps[localKeyId(slotKeyId, variantKeyId)];
      },
      remember,
      keyFor: (slotId, variantId) =>
        slotKeyFor(isLocal(slotId), slotId, variantThreads ? variantId : undefined),
      markLocal,
    }),
    [isLocal, snaps, remember, markLocal, variantThreads],
  );
}
